/**
 * QL-281 — Goal List component (advisor entry).
 *
 * Lists every goal of the flow instance with edit/delete actions and an
 * add / edit modal that saves only on Save. Global pages only. Goals come from the shared goals query
 * (the same cache the goal tab strip reads), so the strip and the list never
 * disagree; mutations go through the internal goal endpoints and invalidate
 * that query plus the completion preflight, exactly like the tab strip does.
 */

import { pickT } from "@repo/shared";
import type { GoalTypeOption } from "@repo/shared/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiErrorMessage } from "@/lib/api/_client";
import * as goalsApi from "@/lib/api/flow-instances/goals";
import { useInvalidateFlowCompletion } from "@/lib/hooks/use-flow-instances/use-flow-completion";
import { useApplyGoalRiskInheritance } from "@/lib/hooks/use-flow-instances/use-goal-risk-inheritance";
import { useInvalidateAssetClassForecast } from "@/lib/hooks/use-flow-instances/use-goal-snapshots";
import { goalQueryKeys, useFlowInstanceGoals } from "@/lib/hooks/use-flow-instances/use-goals";
import type { GoalListConfig } from "@/lib/types/flow";
import { T } from "./goal-list.i18n";
import { GoalListView } from "./goal-list-view";
import type { GoalListCreateData, GoalListGoal, GoalListUpdateData } from "./types";

type GoalListProps = {
  instanceId: string;
  goalTypes: GoalTypeOption[];
  config: GoalListConfig;
  language: string;
  disabled: boolean;
  multiGoal: boolean;
  /** QL-188 — group answering note; null for non-group clients. */
  groupNote?: string | null;
  maxGoals: number;
};

export function GoalList({
  instanceId,
  goalTypes,
  config,
  language,
  disabled,
  multiGoal,
  groupNote = null,
  maxGoals,
}: GoalListProps) {
  const t = pickT(T, language);
  const queryClient = useQueryClient();
  const invalidateCompletion = useInvalidateFlowCompletion();
  const invalidateAssetClassForecast = useInvalidateAssetClassForecast();
  const applyRiskInheritance = useApplyGoalRiskInheritance(instanceId);
  const { goals, isLoading } = useFlowInstanceGoals(instanceId);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: goalQueryKeys.goals(instanceId) });
    // A goal grows / shrinks the engine's fan-out — refresh markers + progress.
    invalidateCompletion(instanceId);
    // QL-315 — a goal added or removed changes the instance-wide asset-class
    // mix, which the server regenerates on both of those paths.
    invalidateAssetClassForecast();
  };

  const createGoal = useMutation({
    mutationFn: (data: GoalListCreateData) =>
      goalsApi.createGoal(instanceId, { ...data, lang: language }),
    onSuccess: (newGoal) => {
      invalidate();
      // QL-212 (D13) — a goal born scored under a client-level risk model moves the
      // same queries a risk submit does. Update and delete carry no such hints.
      applyRiskInheritance(newGoal.id, newGoal.riskInheritance);
    },
  });
  const updateGoal = useMutation({
    mutationFn: ({ goalId, data }: { goalId: string; data: GoalListUpdateData }) =>
      goalsApi.updateGoal(goalId, data),
    onSuccess: invalidate,
  });
  const deleteGoal = useMutation({
    mutationFn: (goalId: string) => goalsApi.deleteGoal(goalId),
    onSuccess: invalidate,
  });

  if (isLoading) return null;

  return (
    <GoalListView
      flowInstanceId={instanceId}
      goals={(goals as GoalListGoal[]) ?? []}
      goalTypes={goalTypes}
      config={config}
      language={language}
      disabled={disabled}
      multiGoal={multiGoal}
      groupNote={groupNote}
      maxGoals={maxGoals}
      isPending={createGoal.isPending || updateGoal.isPending || deleteGoal.isPending}
      onCreate={async (data) => {
        try {
          await createGoal.mutateAsync(data);
          return { success: true };
        } catch (err) {
          return { success: false, error: apiErrorMessage(err, t.failedToCreateGoal) };
        }
      }}
      onUpdate={async (goalId, data) => {
        try {
          const saved = await updateGoal.mutateAsync({ goalId, data });
          return { success: true, platformSync: saved?.platformSync };
        } catch (err) {
          return { success: false, error: apiErrorMessage(err, t.failedToUpdateGoal) };
        }
      }}
      onDelete={async (goalId) => {
        try {
          await deleteGoal.mutateAsync(goalId);
          return { success: true };
        } catch (err) {
          return { success: false, error: apiErrorMessage(err, t.failedToDeleteGoal) };
        }
      }}
    />
  );
}
